const NUMBER_TOLERANCE = 0.000001;

export const DEFAULT_RANK_VALUE = {
  rank_direction: "desc",
  skill_rank_male: [
    { rank: 1, start: 0, end: 50 },
    { rank: 2, start: 50, end: 70 },
    { rank: 3, start: 70, end: 80 },
    { rank: 4, start: 80, end: 90 },
    { rank: 5, start: 90 },
  ],
  skill_rank_female: [
    { rank: 1, start: 0, end: 50 },
    { rank: 2, start: 50, end: 70 },
    { rank: 3, start: 70, end: 80 },
    { rank: 4, start: 80, end: 90 },
    { rank: 5, start: 90 },
  ],
};

export function cloneRankValue(value = DEFAULT_RANK_VALUE) {
  return {
    rank_direction: value?.rank_direction === "asc" ? "asc" : "desc",
    skill_rank_male: normalizeRankList(value?.skill_rank_male),
    skill_rank_female: normalizeRankList(value?.skill_rank_female),
  };
}

function normalizeRankList(ranks) {
  if (!Array.isArray(ranks)) {
    return [];
  }
  return ranks.map((item) => {
    const next = { rank: Number(item.rank) };
    if (hasNumber(item.start)) {
      next.start = Number(item.start);
    }
    if (hasNumber(item.end)) {
      next.end = Number(item.end);
    }
    return next;
  });
}

function hasNumber(value) {
  return value !== undefined && value !== null && value !== "";
}

export function parseNumber(raw) {
  if (!hasNumber(raw)) {
    return null;
  }
  const number = Number(String(raw).trim());
  if (Number.isNaN(number)) {
    return null;
  }
  return number;
}

function numbersMatch(left, right) {
  return Math.abs(Number(left) - Number(right)) < NUMBER_TOLERANCE;
}

function sortByRank(ranks) {
  return [...ranks].sort((a, b) => Number(a.rank) - Number(b.rank));
}

export function isContiguousChain(ranks) {
  if (!Array.isArray(ranks) || ranks.length === 0) {
    return false;
  }
  const sorted = sortByRank(ranks);
  for (let index = 0; index < sorted.length; index += 1) {
    if (Number(sorted[index].rank) !== index + 1) {
      return false;
    }
  }
  if (!hasNumber(sorted[0].start)) {
    return false;
  }
  const lastIndex = sorted.length - 1;
  for (let index = 0; index < lastIndex; index += 1) {
    if (!hasNumber(sorted[index].end) || !hasNumber(sorted[index + 1].start)) {
      return false;
    }
    if (!numbersMatch(sorted[index].end, sorted[index + 1].start)) {
      return false;
    }
  }
  return !hasNumber(sorted[lastIndex].end);
}

export function ranksToBoundaries(ranks) {
  const sorted = sortByRank(ranks);
  const boundaries = [sorted[0].start];
  for (let index = 0; index < sorted.length - 1; index += 1) {
    boundaries.push(sorted[index].end);
  }
  return boundaries;
}

export function boundariesToRanks(rawValues) {
  const points = rawValues
    .map((value) => parseNumber(value))
    .filter((value) => value !== null);
  if (points.length === 0) {
    return [];
  }
  if (points.length === 1) {
    return [{ rank: 1, start: points[0] }];
  }
  const ranks = [];
  for (let index = 0; index < points.length - 1; index += 1) {
    ranks.push({
      rank: index + 1,
      start: points[index],
      end: points[index + 1],
    });
  }
  ranks.push({
    rank: points.length,
    start: points[points.length - 1],
  });
  return ranks;
}

export function canUseBoundaryMode(male, female) {
  if (!isContiguousChain(male) || !isContiguousChain(female)) {
    return false;
  }
  return ranksToBoundaries(male).length === ranksToBoundaries(female).length;
}

function toInput(value) {
  if (!hasNumber(value)) {
    return "";
  }
  return String(value);
}

function toAdvancedRows(male, female) {
  const rankSet = new Set();
  male.forEach((item) => rankSet.add(Number(item.rank)));
  female.forEach((item) => rankSet.add(Number(item.rank)));
  const ranks = [...rankSet].filter((rank) => !Number.isNaN(rank)).sort((a, b) => a - b);
  return ranks.map((rank) => {
    const maleRank = male.find((item) => Number(item.rank) === rank) || {};
    const femaleRank = female.find((item) => Number(item.rank) === rank) || {};
    return {
      rank,
      maleStart: toInput(maleRank.start),
      maleEnd: toInput(maleRank.end),
      femaleStart: toInput(femaleRank.start),
      femaleEnd: toInput(femaleRank.end),
    };
  });
}

export function buildView(value) {
  const rankValue = cloneRankValue(value);
  if (canUseBoundaryMode(rankValue.skill_rank_male, rankValue.skill_rank_female)) {
    const maleBounds = ranksToBoundaries(rankValue.skill_rank_male);
    const femaleBounds = ranksToBoundaries(rankValue.skill_rank_female);
    return {
      mode: "boundary",
      rank_direction: rankValue.rank_direction,
      rows: maleBounds.map((maleValue, index) => ({
        male: toInput(maleValue),
        female: toInput(femaleBounds[index]),
      })),
      advancedRows: [],
    };
  }
  return {
    mode: "advanced",
    rank_direction: rankValue.rank_direction,
    rows: [],
    advancedRows: toAdvancedRows(rankValue.skill_rank_male, rankValue.skill_rank_female),
  };
}

function advancedToRanks(rows, gender) {
  const startKey = `${gender}Start`;
  const endKey = `${gender}End`;
  return rows.reduce((ranks, row) => {
    const start = parseNumber(row[startKey]);
    const end = parseNumber(row[endKey]);
    if (start === null && end === null) {
      return ranks;
    }
    const item = { rank: Number(row.rank) };
    if (start !== null) {
      item.start = start;
    }
    if (end !== null) {
      item.end = end;
    }
    ranks.push(item);
    return ranks;
  }, []);
}

export function viewToValue(view) {
  if (view.mode === "boundary") {
    return {
      rank_direction: view.rank_direction === "asc" ? "asc" : "desc",
      skill_rank_male: boundariesToRanks(view.rows.map((row) => row.male)),
      skill_rank_female: boundariesToRanks(view.rows.map((row) => row.female)),
    };
  }
  return {
    rank_direction: view.rank_direction === "asc" ? "asc" : "desc",
    skill_rank_male: advancedToRanks(view.advancedRows, "male"),
    skill_rank_female: advancedToRanks(view.advancedRows, "female"),
  };
}

export function formatRankBands(ranks) {
  if (!Array.isArray(ranks) || ranks.length === 0) {
    return "未設定";
  }
  return ranks
    .map((item) => {
      const hasStart = hasNumber(item.start);
      const hasEnd = hasNumber(item.end);
      if (hasStart && hasEnd) {
        return `${item.rank}級 ${item.start}–${item.end}`;
      }
      if (hasStart) {
        return `${item.rank}級 ${item.start} 或以上`;
      }
      if (hasEnd) {
        return `${item.rank}級 ${item.end} 以下`;
      }
      return `${item.rank}級`;
    })
    .join("，");
}

export function boundaryRowLabel(index) {
  return index === 0 ? "Base" : String(index);
}
